import type { StateSnapshot } from "@touchscreen/api-client";

export interface PollScheduleOptions {
  /** Floor between polls, per touchscreen-api.md: 10–15s so other punters' requests surface promptly. */
  floorMs?: number;
  /** Ceiling for the "normal" schedule — guards against a bad `duration_seconds` scheduling a huge gap. */
  maxNormalDelayMs?: number;
  /** Starting backoff delay once the venue is closed, the player is offline, or a poll errored. */
  backoffStartMs?: number;
  backoffMaxMs?: number;
}

export interface BackoffState {
  ms: number;
}

const DEFAULT_BACKOFF_START_MS = 15_000;

/** Mutable backoff cursor a caller keeps alongside the query (e.g. in a `useRef`). */
export function createBackoffState(startMs: number = DEFAULT_BACKOFF_START_MS): BackoffState {
  return { ms: startMs };
}

/**
 * Pure scheduling math for TanStack Query's `refetchInterval`, ported from the previous
 * `PollEngine` class: poll shortly after the current track is due to end, floored at 10–15s, and
 * back off exponentially when the venue is closed, the player is offline, or the last poll
 * errored. A kiosk should never stop calling this — Studio derives "kiosk last seen" from the
 * polling itself. A phone should disable the query on `visibilitychange` to hidden instead of
 * relying on this to slow down.
 */
export function computeNextPollDelayMs(
  snapshot: StateSnapshot | null,
  hadError: boolean,
  backoff: BackoffState,
  options?: PollScheduleOptions,
): number {
  const floorMs = options?.floorMs ?? 12_000;
  const maxNormalDelayMs = options?.maxNormalDelayMs ?? 5 * 60_000;
  const backoffStartMs = options?.backoffStartMs ?? DEFAULT_BACKOFF_START_MS;
  const backoffMaxMs = options?.backoffMaxMs ?? 60_000;

  if (hadError || !snapshot || !snapshot.requests.open || !snapshot.player.online) {
    const delay = backoff.ms;
    backoff.ms = Math.min(backoffMaxMs, backoff.ms * 2);
    return delay;
  }

  backoff.ms = backoffStartMs;

  const track = snapshot.now_playing;
  if (!track) return floorMs;

  const startedAtMs = Date.parse(track.started_at);
  const predictedEndMs = startedAtMs + track.duration_seconds * 1000;
  const untilEndMs = predictedEndMs - Date.now();

  return clamp(untilEndMs, floorMs, maxNormalDelayMs);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
